import { type ReactNode } from 'react';
import { cn } from '@/lib/cn';

export interface Column<T> {
  key: string;
  header: string;
  cell?: (row: T) => ReactNode;
  align?: 'left' | 'right' | 'center';
  width?: string;
  className?: string;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[] | undefined;
  loading?: boolean;
  error?: string | null;
  empty?: string;
  rowKey?: (row: T, i: number) => string | number;
}

export function DataTable<T>({
  columns,
  rows,
  loading,
  error,
  empty = 'No rows.',
  rowKey,
}: DataTableProps<T>): JSX.Element {
  if (error) {
    return (
      <div className="rounded-md border border-state-danger bg-surface-raised px-3 py-2 text-sm text-state-danger">
        {error}
      </div>
    );
  }
  if (loading && !rows) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="h-10 animate-pulse rounded-md bg-surface-divider/30"
          />
        ))}
      </div>
    );
  }
  if (!rows || rows.length === 0) {
    return (
      <div className="rounded-md border border-surface-divider bg-surface-raised px-3 py-6 text-center text-sm text-fg-subtle">
        {empty}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-separate border-spacing-0 text-sm">
        <thead>
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                style={c.width ? { width: c.width } : undefined}
                className={cn(
                  'sticky top-0 bg-surface-raised px-3 py-2 text-xs font-bold uppercase tracking-wide text-fg-muted',
                  'border-b border-surface-divider',
                  c.align === 'right' && 'text-right',
                  c.align === 'center' && 'text-center',
                  !c.align && 'text-left',
                )}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={rowKey ? rowKey(row, i) : i}
              className="group hover:bg-surface-raised/60"
            >
              {columns.map((c) => {
                const raw = (row as Record<string, unknown>)[c.key];
                const content = c.cell
                  ? c.cell(row)
                  : raw === null || raw === undefined
                    ? ''
                    : String(raw);
                return (
                  <td
                    key={c.key}
                    className={cn(
                      'px-3 py-3 border-b border-surface-divider/60 align-middle',
                      c.align === 'right' && 'text-right tabular-nums',
                      c.align === 'center' && 'text-center',
                      c.className,
                    )}
                  >
                    {content}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
